import { useCallback } from 'react';

import { useTranslation } from 'react-i18next';
import { DeliveryBlockResource } from '@bringg/types';

import { useStores } from 'bringg-web/recipes';
import { ResourceWithBlockData } from 'bringg-web/features/optimization-wizard/types';

const DRIVER_NAME = 'driverName';
const VEHICLE_NAME = 'vehicleName';
const TRAILER_NAME = 'trailerName';
export const RESOURCE_NAMES = 'resourceNames';
const SERVICE_AREA_NAME = 'serviceAreas';
const AVOID_AREA_NAME = 'avoidAreas';

export interface ResourcesTableColumns {
	[DRIVER_NAME]?: string;
	[VEHICLE_NAME]?: string;
	[TRAILER_NAME]?: string;
}

interface UseResourceTableOptions {
	showDriversColumn?: boolean;
	showTrailersColumn?: boolean;
	showServiceAreasColumn?: boolean;
}

export const UseResourceTable = (
	deliveryBlocksResources: DeliveryBlockResource[] | ResourceWithBlockData[],
	options?: UseResourceTableOptions
) => {
	const { merchantConfigurationsStore, driversStore, vehiclesStore } = useStores();
	const { t } = useTranslation();

	const getResourceColumns = useCallback(() => {
		const columns = [];
		const resourceWithDriver = deliveryBlocksResources.find(resource => resource.user_id);

		if (options?.showDriversColumn || resourceWithDriver) {
			columns.push({
				Header: t('RESOURCES_TABLE.DRIVER_NAME'),
				accessor: DRIVER_NAME,
				id: DRIVER_NAME,
				filter: 'byName'
			});
		}
		if (merchantConfigurationsStore.configuration.enable_vehicles) {
			columns.push({
				Header: t('RESOURCES_TABLE.VEHICLE_NAME'),
				accessor: VEHICLE_NAME,
				id: VEHICLE_NAME,
				filter: 'byName'
			});

			const resourceWithTrailer = deliveryBlocksResources.find(resource => {
				if (resource.trailer_id) {
					return true;
				}
				const vehicle = vehiclesStore.get(resource.vehicle_id);
				return resource.use_default_trailer && vehicle?.trailer;
			});

			if (options?.showTrailersColumn || resourceWithTrailer) {
				columns.push({
					Header: t('RESOURCES_TABLE.TRAILER_NAME'),
					accessor: TRAILER_NAME,
					id: TRAILER_NAME,
					filter: 'byName'
				});
			}
		}
		if (options?.showServiceAreasColumn) {
			columns.push(
				{
					Header: t('DELIVERY_BLOCKS.SERVICE_AREAS'),
					accessor: SERVICE_AREA_NAME,
					filter: 'byName',
					id: SERVICE_AREA_NAME
				},
				{
					Header: t('DELIVERY_BLOCKS.AVOID_AREAS'),
					accessor: AVOID_AREA_NAME,
					filter: 'byName',
					id: AVOID_AREA_NAME
				}
			);
		}

		return columns;
	}, [deliveryBlocksResources, merchantConfigurationsStore.configuration.enable_vehicles, options, t]);

	const getResourceData = useCallback(
		<T extends DeliveryBlockResource>(resource: T) => {
			const vehicle = vehiclesStore.get(resource.vehicle_id);
			let trailerName = '-';
			if (resource.trailer_id) {
				trailerName = vehiclesStore.get(resource.trailer_id)?.name;
			} else if (resource.use_default_trailer && vehicle?.trailer) {
				trailerName = vehicle.trailer.name;
			}

			const driverName = driversStore.get(resource?.user_id)?.name;
			const vehicleName = vehiclesStore.get(resource?.vehicle_id)?.name;

			return {
				[DRIVER_NAME]: driverName || '-',
				[VEHICLE_NAME]: vehicleName || '-',
				[TRAILER_NAME]: trailerName || '-',
				[RESOURCE_NAMES]: {
					driver: driverName,
					vehicle: vehicleName
				}
			};
		},
		[driversStore, vehiclesStore]
	);

	return { getResourceColumns, getResourceData };
};
